// L'APPARITION DES SECTIONS AU DÉFILEMENT — voir SiteSetting::MOTION_CLASSES.
//
// CE QU'IL FAIT : chaque section de bloc part légèrement décalée et transparente, puis se met en
// place quand elle entre à l'écran. La feuille tient les deux états ; ce script ne fait que dire
// quand passer de l'un à l'autre.
//
// ════════════════════════════════════════════════════════════════════════════════════════════════
// LE PIÈGE QUE CE FICHIER EXISTE POUR ÉVITER : une page entièrement invisible.
//
// L'écriture naturelle serait de poser l'état de départ dans le gabarit, et de le retirer ici. Elle
// est fausse : le jour où ce script ne tourne pas — erreur de chargement, navigateur ancien, coupure
// pendant le téléchargement du paquet —, plus rien ne retire l'état de départ, et le visiteur reçoit
// une page blanche dont RIEN n'indique qu'elle porte du texte.
//
// C'est donc le script qui POSE l'état de départ, au tout début, et seulement s'il tourne. Une page
// servie sans lui garde toutes ses sections visibles : elle perd un effet, pas son contenu.
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// L'OBSERVATEUR PLUTÔT QU'UN ÉCOUTEUR DE DÉFILEMENT : `IntersectionObserver` ne réveille le fil
// principal que lorsqu'un élément franchit le seuil, là où un `scroll` se déclenche à chaque image
// et fait sauter le défilement sur un téléphone.
//
// ON N'OBSERVE PLUS CE QUI EST APPARU : la section est détachée dès qu'elle s'est montrée. Sans
// cela, remonter la page la ferait disparaître puis réapparaître — un effet que personne n'attend,
// et qui rend la lecture pénible sur une page qu'on parcourt de haut en bas puis de bas en haut.

const CLASSE_SITE = "motion-reveal"
const CLASSE_DEPART = "to-reveal"
const CLASSE_ARRIVEE = "seen"

// La section doit être entrée d'un huitième pour compter : à zéro, un seul pixel visible en bas de
// l'écran déclencherait l'apparition, qui se jouerait alors hors de vue.
const SEUIL = 0.12

// Le délai au bout duquel on montre tout, quoi qu'il arrive — voir le filet, plus bas.
const DELAI_DE_SECOURS = 3000

function revelerLesSections() {
  // RIEN SANS LE RÉGLAGE : la classe est posée par le gabarit d'après ce que la compagnie a choisi.
  if (!document.body?.classList.contains(CLASSE_SITE)) return

  // QUI A DEMANDÉ MOINS D'ANIMATION N'EN REÇOIT AUCUNE — et surtout, on ne pose alors même pas
  // l'état de départ. La feuille le neutraliserait de toute façon, mais le poser puis l'annuler
  // laisserait une image de page décalée le temps que le style s'applique.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

  const sections = document.querySelectorAll(".site-main .block-section")
  if (sections.length === 0) return

  // LA PREMIÈRE SECTION NE S'ANIME PAS : elle est déjà à l'écran au chargement, et la faire monter
  // ferait sauter le haut de page sous les yeux de quelqu'un qui n'a pas encore commencé à lire.
  const aReveler = [...sections].slice(1)
  aReveler.forEach((section) => section.classList.add(CLASSE_DEPART))

  const observateur = new IntersectionObserver(
    (entrees) => {
      entrees.forEach((entree) => {
        if (!entree.isIntersecting) return

        entree.target.classList.add(CLASSE_ARRIVEE)
        observateur.unobserve(entree.target)
      })
    },
    { threshold: SEUIL }
  )

  aReveler.forEach((section) => observateur.observe(section))

  // ══════════════════════════════════════════════════════════════════════════════════════════════
  // LE FILET, ET IL N'EST PAS THÉORIQUE.
  //
  // Poser l'état de départ ici plutôt que dans le gabarit protège du cas « le script ne tourne
  // pas ». Il reste l'autre : le script tourne, cache les sections, et l'observateur ne les révèle
  // jamais. Vu à l'écran le 22 août — dans un onglet en arrière-plan, le navigateur ne calcule plus
  // d'intersection, et la page servie était ENTIÈREMENT BLANCHE le temps qu'on y revienne.
  //
  // Cet onglet-là finit par se réveiller. Une exception levée ailleurs dans le paquet, un
  // observateur qui ne se déclenche pas sur un navigateur qu'on n'a pas testé, un élément détaché
  // par Turbo au mauvais moment : ceux-là ne se réveillent pas.
  //
  // AU BOUT DE TROIS SECONDES, TOUT CE QUI ATTEND ENCORE EST MONTRÉ. C'est bien plus long qu'un
  // défilement normal — l'effet joue son rôle dans tous les cas ordinaires — et bien plus court que
  // la patience de quelqu'un devant une page vide.
  //
  // On ne cherche PAS à savoir pourquoi une section n'est pas apparue : le filet ne diagnostique
  // rien, il rend le texte lisible. Le diagnostic, lui, part au journal.
  // ══════════════════════════════════════════════════════════════════════════════════════════════
  window.setTimeout(() => {
    const restantes = aReveler.filter((section) => !section.classList.contains(CLASSE_ARRIVEE))
    if (restantes.length === 0) return

    restantes.forEach((section) => {
      section.classList.add(CLASSE_ARRIVEE)
      observateur.unobserve(section)
    })

    // JAMAIS EN SILENCE : si ce filet sert, c'est qu'une hypothèse est fausse, et personne ne le
    // saurait autrement — la page a l'air normale une fois révélée.
    console.warn(
      `[apparition] ${restantes.length} section(s) révélée(s) par le filet de sécurité : ` +
        "l'observateur ne les avait pas signalées après 3 s."
    )
  }, DELAI_DE_SECOURS)
}

// `turbo:load` ET NON `DOMContentLoaded` : Turbo remplace le corps du document à chaque navigation
// sans recharger la page, et un écouteur posé sur le chargement initial ne se rejouerait jamais —
// la deuxième page visitée n'aurait plus d'apparition.
document.addEventListener("turbo:load", revelerLesSections)
